import React, {useCallback, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {AxiosError} from "axios";
import {ProgressSpinner} from "primereact/progressspinner";

import {useToast} from "../../../hooks/useToast";
import {HttpService} from "../../../services/HttpService";
import {stepsKeys,} from "../../../components/DocumentGeneration/types";
import StepsControl from "../../../components/DocumentGeneration/components/StepsControl/StepsControl";
import {transportTypes} from "../../../config/config";
import {extractErrorMessage} from "../../../helpers/extractErrorMessage";

import Terms from "./Terms";
import Preview from "./Preview";
import Payment from "./Payment";
import SignWell from "./SignWell";
import FormFile15620 from "./FormFile15620";
import DeliveryService from "./DeliveryService";
import moment from 'moment';
import isValidUSState from "../../../helpers/us_states";
import {ConfirmDialog} from "primereact/confirmdialog";


type PagerProps = {
    email?: string;
    price: number;
    uspsPrice: number;
    isPublic: boolean;
    companyId:number;
    isDataLoaded?: boolean;
    paymentEnabled:boolean;
    isPublicOpenAi:boolean;
    templateId:string |undefined;
    deliveryService:string;
    formData: Record<string, string | number>;
}

function  FormWrapper15620(props:PagerProps) {
    const {
        email,
        price,
        uspsPrice,
        isPublic,
        companyId,
        formData,
        templateId,
        isDataLoaded,
        paymentEnabled,
        isPublicOpenAi,
        deliveryService
    
    } = props;
    const { show } = useToast();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isLoaded, setLoaded] = useState(false);
    const [showDeliveryService, setShowDeliveryService] = useState(deliveryService === 'NONE');
    const [fileId, setFieldId] = useState(searchParams.get('fileId'));
    const [isUpdateMode, setUpdateMode] = useState(searchParams.has('fileId'));
    const [isLastStep, setIsLastStep] = useState<boolean>(Boolean(searchParams.get('rdn')));
    const [step, setStep] = useState(isLastStep ? stepsKeys.finish : stepsKeys.filling);
    const [source, setSource] = useState<Record<string, string>>({});
    const [formValues, setFormValues] = useState<Record<string, any>>(formData);
    const [countryStateWarning, setCountryStateWarning] = useState(false);
    const [selectedDeliveryService, setSelectedDeliveryService] = useState(() => {
        if(isUpdateMode && formData.deliveryService) {
            return String(formData.deliveryService)
        }
        return deliveryService === 'NONE' ? transportTypes.FEDEX : deliveryService;
    });


    const submitHandler = useCallback((formData: any) => {
        setLoaded(true);
        const params = {
            templateId,
            deliveryService: selectedDeliveryService,
            replacements: { ...formData, firstRecipientEmail: email }
        };

    //@ts-ignore
    const { date_of_transfer } = params.replacements;
    const maxPastDays = formData.maxDaysPastTransfer || 14;

    const currentDate = moment().utc();
    const cutoffDate = currentDate.clone().subtract(maxPastDays, 'days');
    const formatedDate = moment(date_of_transfer).format("MMMM D, YYYY");
    const transferDate = moment.utc(formatedDate, "MMMM D, YYYY");
  
    if (!transferDate.isBetween(cutoffDate, currentDate, "days", "[]")) {
      show({
        severity: 'error',
        detail:"The grant date for this record exceeds the cutoff period. Unfortunately, it is no longer eligible for processing through Corpora."
      });
      setStep(stepsKeys.filling);
      return;
    }


        setFormValues(formData);
        const url = isUpdateMode ? `/api/companies/${companyId}/file83b/${fileId}/generate-pdf` : `/api/companies/${companyId}/file83b/generate-pdf`;

        //@ts-ignore
    params.replacements.date_of_transfer = formatedDate;

        HttpService.post(
            url,
            { ...params }
        ).then(res => {
            setSource(res.data);
            setFieldId(res.data.formId);
            setStep((currentStep) => {
                if (currentStep === stepsKeys.terms) {
                    return paymentEnabled ? stepsKeys.pay : stepsKeys.finish
                }

                return currentStep;

            });

        }).catch((error: AxiosError<{ message: string }>) => {
            extractErrorMessage({
                error,
                show: show,
            });
        }).finally(() => {
            setLoaded(false);
        })

    }, [companyId, templateId, step, selectedDeliveryService, showDeliveryService, paymentEnabled]);

    const handleSaveData = useCallback((data:any) => {
        const {country, state} = data;
        const isUSSelected = country === 'United States';
        
        setFormValues(data);
        if(!isUSSelected && isValidUSState(state as string)) {
            setCountryStateWarning(true);
        } else {
            setStep(stepsKeys.review);
        }

    }, []);

    return (
        <div className="w-full h-full flex flex-column gap-4">
            {
                isDataLoaded ? (
                    <>
                        <div className="w-full flex flex-column flex-row align-items-center">
                            <StepsControl
                                step={step}
                                isPublic={isPublic}
                                paymentEnabled={paymentEnabled}
                                isPublicOpenAi={isPublicOpenAi}
                                showDeliveryService={paymentEnabled}
                                {...(isLastStep ? {
                                    isFLowCompleted:true,
                                    afterEmbeddedPay:true
                                } : {})}
                            />
                        </div>

                        <div className="w-full h-full flex flex-column flex-row align-items-center">
                            {
                                step === stepsKeys.filling && (
                                    <FormFile15620  
                                                    
                                                    templateId={templateId as string}
                                                    deliveryService={selectedDeliveryService}
                                                    firstRecipientEmail={email as string}
                                                    formData={formValues}
                                                    fileId={fileId}
                                                    companyId={companyId}
                                                    onDataSave={handleSaveData}/>
                                )
                            }
                            {
                                step === stepsKeys.review && (
                                    <Preview
                                            formData={formValues}                                             
                                            handleNextStep={() => setStep(stepsKeys.terms)}
                                            handlePreviousStep={() => {
                                                setStep(stepsKeys.filling);
                                                navigate(`?fileId=${formData.id || fileId}`)
                                             }}
                                    />
                                )
                            }

                            {
                                step === stepsKeys.terms && (
                                    <Terms 
                                        isSubmitted={isLoaded}
                                        handleNextStep={() => {
                                            submitHandler({ ...formValues });
                                        }}
                                        handlePreviousStep={() => setStep(stepsKeys.review)} />
                                )
                            }
                            {/*{*/}
                            {/*    (step === stepsKeys.delivery) && (*/}
                            {/*        <DeliveryService*/}
                            {/*                    fedexPrice={price}*/}
                            {/*                    uspsPrice={uspsPrice}*/}
                            {/*                    deliveryService={selectedDeliveryService}*/}
                            {/*                    handleNextStep={()=> {*/}
                            {/*                        submitHandler({ ...formValues });*/}
                            {/*                    }}*/}
                            {/*                    handlePreviousStep={() => setStep(stepsKeys.terms)} */}
                            {/*                    setSelectedDeliveryService={setSelectedDeliveryService}/>*/}
                            {/*    )*/}
                            {/*}*/}
                            {
                                step === stepsKeys.pay && (
                                    <Payment uuid={source.fileUUID}
                                             companyId={companyId}
                                             handlePreviousStep={() => setStep(stepsKeys.filling)}/>
                                )
                            }
                            {
                                 (step === stepsKeys.finish) && (
                                    <SignWell embeddedUrl={source.embeddedSigningUrl} companyId={companyId}/>
                                )
                            }
                        </div>
                    </>
                ) : (
                    <div className="w-full h-full flex flex-column justify-content-center align-items-center">
                        <ProgressSpinner style={{ width: '50px', height:'50px' }}  strokeWidth="4"/>
                    </div>
                )
            }
            <ConfirmDialog
                visible={countryStateWarning}
                onHide={
                    () => setCountryStateWarning(false)
                }
                message="Did you intend to insert a U.S. address?  If so, you’ve provided a different country in the “Country” field."
                header="Warning"
                icon="pi pi-check"
                rejectLabel="Go back and fix"
                acceptLabel="Proceed"
                accept={() => {
                    setStep(stepsKeys.review);
                }}
            />
        </div>
    )
}

export default FormWrapper15620;